import type { ContratCxpiCloneDTO, ContratCxpiCreateDTO } from '@/features/api/extranet/dto/Demande/CXPI'
import type { HabilitationDispositifDTO, HabilitationsDTO } from '@/features/api/extranet/dto/Demande/shared'
import { ONGLET_EMPLOYEUR } from '@/features/api/extranet/dto/Demande/shared'
import type { RattachementDTO } from '@/features/api/extranet/dto/Profil'
import type { EtablissementAdherentDTO } from '@/features/api/extranet/dto/shared'
import type { MachineEtatOptions, Runnable, Transition, Transitions, TransitionOut } from '@/features/domaine/machine-etats'
import { idccAutoriseTT } from '@/features/domaine/regles-akto/shared'
import { SousDispositifDRF } from '@/features/domaine/types/opco/Demande/CXPI'
import ChoixTypePublic from '@/features/interface/demande/ajouter-drf/cartes/ChoixTypePublic.vue'
import type { InformationMessageIn } from '@/features/interface/demande/ajouter-drf/cartes/InformationMessage.type.ts'
import InformationMessage from '@/features/interface/demande/ajouter-drf/cartes/InformationMessage.vue'
import type {
  CloneDrfLaunch,
  CreationDrfHabilitationsChargées,
  CreationDrfLaunch,
  CreationDrfRattachementsChargés,
  CreationDrfResult,
  CreationDrfResultChoixEtablissement,
  CreationDrfResultChoixIdcc,
  CreationDrfResultChoixRattachement,
  CreationDrfResultChoixSousDispositif,
  CreationDrfResultChoixTypePublic,
} from '@/features/interface/demande/ajouter-drf/types.ts'
import ChoixEtablissementModeAdherent from '@/features/interface/demande/ajouter/cartes/ChoixEtablissementModeAdherent.vue'
import ChoixIdcc from '@/features/interface/demande/ajouter/cartes/ChoixIdcc.vue'
import ChoixOnglets from '@/features/interface/demande/ajouter/cartes/ChoixOnglets.vue'
import ChoixRattachement from '@/features/interface/demande/ajouter/cartes/ChoixRattachement.vue'
import ChoixSousDispositif from '@/features/interface/demande/ajouter/cartes/ChoixSousDispositif.vue'
import type { SelectionnerOngletsIn, SelectionnerOngletsOut } from '@/features/interface/demande/ajouter/CreationDemandeConfiguration.types.ts'
import { fabriqueRunCarte, fusionneEtats } from '@/features/interface/machine-etats/tools.ts'
import { useMachineEtatDisplay } from '@/features/interface/machine-etats/useMachineEtat.ts'
import allServices from '@/features/services/demande/DemandeAllService.ts'
import { loadRattachements } from '@/features/services/profil/RattachementUtilisateurServices.ts'

function estHabilitéCXPI(habilitations: HabilitationsDTO) {
  return (
    habilitations.typePublics.some((type) => type.code === 'Interimaire') &&
    habilitations.dispositifs.some((dispo) => dispo.code === 'FPE')
  )
}

const transitionMessageInfo: Runnable<InformationMessageIn, void> = { run: fabriqueRunCarte(InformationMessage) }

const ETAT_RATTACHEMENT_CHARGÉS = 'ETAT_RATTACHEMENT_CHARGÉS' as const
const transitionChargerRattachements: Runnable<void, CreationDrfRattachementsChargés> = {
  run: async () => {
    const rattachements = (await loadRattachements(true))
      ?.filter((rattachement) => rattachement.publics.includes('Interimaire'))
      ?.filter((rattachement) =>
        rattachement.permissions.some((permission) => permission.type === 'FPE' && permission.niveau === 'Ecriture'),
      )
    return {
      out: { rattachements: rattachements ?? [] },
      type: 'automatique',
    }
  },
}
const ETAT_RATTACHEMENT_SELECTIONNÉ = 'ETAT_RATTACHEMENT_SELECTIONNÉ' as const
const transitionSelectionnerRattachement: Runnable<RattachementDTO[], CreationDrfResultChoixRattachement> = {
  run: async (options) => {
    /* Todo : Pour debug a réactiver */
    if (options.modelIn.length === 1) {
      const rattachement = options.modelIn[0]!
      return {
        out: { rattachement },
        type: 'automatique',
      }
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixRattachement, ...options })
    const rattachement: RattachementDTO = result.value

    return {
      out: { rattachement },
      transitionState: result.transitionState,
      type: 'interactif',
    }
  },
}

const ETAT_ETABLISSEMENT_SELECTIONNÉ = 'ETAT_ETABLISSEMENT_SELECTIONNÉ' as const
const transitionSelectionnerEtablissement: Runnable<RattachementDTO, CreationDrfResultChoixEtablissement> = {
  run: async (options) => {
    /* Todo : Pour debug a réactiver */
    if (options.modelIn.typeEntiteJuridique === 'Etablissement') {
      const allEtablissements = await allServices.searchEtablissementAdherent(
        options.modelIn.identifiantEntiteJuridique,
      )
      const etablissement = allEtablissements.at(0)

      if (etablissement?.etatAdministratif === 'A') {
        return {
          out: { etablissement },
          type: 'automatique',
        }
      }
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixEtablissementModeAdherent, ...options })
    const etablissement: EtablissementAdherentDTO = result.value

    return {
      out: { etablissement },
      transitionState: result.transitionState,
      type: 'interactif',
    }
  },
}
// sur transitionMessageInfo
const ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ = 'ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ' as const

const ETAT_IDCC_SELECTIONNÉ = 'ETAT_IDCC_SELECTIONNÉ' as const
const transitionSelectionnerIdcc: Runnable<EtablissementAdherentDTO, CreationDrfResultChoixIdcc> = {
  run: async (options) => {
    if (options.modelIn.idcc) {
      return {
        out: { idcc: options.modelIn.idcc },
        type: 'automatique',
      }
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixIdcc, ...options })

    return {
      out: { idcc: result.value },
      transitionState: result.transitionState,
      type: 'interactif',
    }
  },
}

// sur transitionMessageInfo
const ETAT_IDCC_NON_TT_SELECTIONNÉ = 'ETAT_IDCC_NON_TT_SELECTIONNÉ' as const

const ETAT_HABILITATIONS_CHARGÉES = 'ETAT_HABILITATIONS_CHARGÉES' as const
export type TransitionChargementHabilitationsIn = { siret: string; idcc: string }
const transitionChargementHabilitations: Runnable<
  TransitionChargementHabilitationsIn,
  CreationDrfHabilitationsChargées
> = {
  run: async (options) => {
    const habilitations = await allServices.getHabilitations('ReservationFonds',options.modelIn.siret, options.modelIn.idcc)
    return {
      out: { habilitations },
      type: 'automatique',
    }
  },
}

// sur transitionMessageInfo
const ETAT_HABILITATIONS_MANQUANTES = 'ETAT_HABILITATIONS_MANQUANTES' as const

const ETAT_TYPE_PUBLIC_SELECTIONNÉ = 'ETAT_TYPE_PUBLIC_SELECTIONNÉ' as const
const transitionSelectionnerTypePublic: Runnable<void, CreationDrfResultChoixTypePublic> = {
  run: async (options) => {
    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixTypePublic, ...options })

    return {
      out: { typePublic: 'Interimaire' },
      transitionState: result.transitionState,
      type: 'interactif',
    }
  },
}

const ETAT_SOUS_DISPOSITIFS_SELECTIONNÉ = 'ETAT_SOUS_DISPOSITIFS_SELECTIONNÉ' as const
const transitionSelectionnerSousDispositif: Runnable<HabilitationDispositifDTO, CreationDrfResultChoixSousDispositif> =
  {
    run: async (options) => {
      if (!options.modelIn.sousDispositifs?.length) {
        throw 'Sous dispositifs CxPI manquants'
      } else if (options.modelIn.sousDispositifs.length === 1) {
        return {
          out: {
            sousDispositif: options.modelIn.sousDispositifs[0],
          },
          type: 'automatique',
        } as TransitionOut<CreationDrfResultChoixSousDispositif>
      }

      const display = useMachineEtatDisplay()
      const result = await display.runComponent({ component: ChoixSousDispositif, ...options })

      return {
        out: {
          sousDispositif: result.value,
        },
        transitionState: result.transitionState,
        type: 'interactif',
      } as TransitionOut<CreationDrfResultChoixSousDispositif>
    },
  }

export const CreationDrfConfiguration: MachineEtatOptions<CreationDrfLaunch, ContratCxpiCreateDTO> = {
  transitions: [
    {
      etatDepart: null,
      etatArrive: ETAT_RATTACHEMENT_CHARGÉS,
      runnable: transitionChargerRattachements,
    } as Transition<void, CreationDrfRattachementsChargés>,
    {
      etatDepart: ETAT_RATTACHEMENT_CHARGÉS,
      etatArrive: ETAT_RATTACHEMENT_SELECTIONNÉ,
      runnable: transitionSelectionnerRattachement,
      queryIn: (options) => {
        return fusionneEtats(options.etats).rattachements
      },
    } as Transition<RattachementDTO[], CreationDrfResultChoixRattachement>,
    {
      etatDepart: ETAT_RATTACHEMENT_SELECTIONNÉ,
      etatArrive: ETAT_ETABLISSEMENT_SELECTIONNÉ,
      runnable: transitionSelectionnerEtablissement,
      queryIn: (options) => {
        return fusionneEtats(options.etats).rattachement
      },
    } as Transition<RattachementDTO, CreationDrfResultChoixEtablissement>,
    {
      etatDepart: ETAT_ETABLISSEMENT_SELECTIONNÉ,
      etatArrive: ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ,
      runnable: transitionMessageInfo,
      if: (options) => {
        const etablissement = fusionneEtats<CreationDrfResult>(options.etats).etablissement
        return etablissement.etatAdministratif !== 'A'
      },
      queryIn: () =>
        'Vous ne pouvez sélectionner un établissement inactif, merci de sélectionner un autre établissement.',
    } as Transition<InformationMessageIn, void>,
    {
      etatDepart: ETAT_ETABLISSEMENT_SELECTIONNÉ,
      etatArrive: ETAT_IDCC_SELECTIONNÉ,
      runnable: transitionSelectionnerIdcc,
      queryIn: (options) => {
        return fusionneEtats(options.etats).etablissement
      },
      if: (options) => {
        const etablissement = fusionneEtats<CreationDrfResult>(options.etats).etablissement
        return etablissement.etatAdministratif === 'A'
      },
    } as Transition<EtablissementAdherentDTO, CreationDrfResultChoixIdcc>,
    {
      etatDepart: ETAT_IDCC_SELECTIONNÉ,
      etatArrive: ETAT_IDCC_NON_TT_SELECTIONNÉ,
      runnable: transitionMessageInfo,
      queryIn: () => "L'Idcc de votre établissement n'est pas éligible au DRF.",
      if: (options) => {
        const etat = fusionneEtats<CreationDrfResult>(options.etats)
        return !idccAutoriseTT.includes(etat.idcc.code)
      },
    } as Transition<InformationMessageIn, void>,
    {
      etatDepart: ETAT_IDCC_SELECTIONNÉ,
      etatArrive: ETAT_HABILITATIONS_CHARGÉES,
      runnable: transitionChargementHabilitations,
      queryIn: (options) => {
        const etat = fusionneEtats<CreationDrfResult>(options.etats)
        return {
          siret: etat.etablissement.siret.valeur,
          idcc: etat.idcc.code,
        }
      },
      if: (options) => {
        const etat = fusionneEtats<CreationDrfResult>(options.etats)
        return idccAutoriseTT.includes(etat.idcc.code)
      },
    } as Transition<TransitionChargementHabilitationsIn, CreationDrfHabilitationsChargées>,
    {
      etatDepart: ETAT_HABILITATIONS_CHARGÉES,
      etatArrive: ETAT_HABILITATIONS_MANQUANTES,
      runnable: transitionMessageInfo,
      queryIn: () => 'Vos habilitations ne permettent pas de créer une DRF pour cet établissement.',
      if: (options) => {
        const etat = fusionneEtats<CreationDrfResult>(options.etats)
        return !estHabilitéCXPI(etat.habilitations)
      },
    } as Transition<InformationMessageIn, void>,
    {
      etatDepart: ETAT_HABILITATIONS_CHARGÉES,
      etatArrive: ETAT_TYPE_PUBLIC_SELECTIONNÉ,
      runnable: transitionSelectionnerTypePublic,
      if: (options) => {
        const etat = fusionneEtats<CreationDrfResult>(options.etats)
        return estHabilitéCXPI(etat.habilitations)
      },
    } as Transition<void, CreationDrfResultChoixTypePublic>,
    {
      etatDepart: ETAT_TYPE_PUBLIC_SELECTIONNÉ,
      etatArrive: ETAT_SOUS_DISPOSITIFS_SELECTIONNÉ,
      runnable: transitionSelectionnerSousDispositif,
      queryIn: (options) => {
        const habilitation = fusionneEtats<CreationDrfResult>(options.etats).habilitations.dispositifs.find(
          (habilitationDispositif) => habilitationDispositif.code === 'FPE',
        )
        habilitation!.sousDispositifs = habilitation!.sousDispositifs.filter((sd) =>
          SousDispositifDRF.includes(sd as SousDispositifDRF),
        )
        habilitation!.typeDemande = 'ReservationFonds'
        return habilitation!
      },
    } as Transition<HabilitationDispositifDTO, CreationDrfResultChoixSousDispositif>,
  ],
  resultCallback: (options) => {
    const etat = fusionneEtats<CreationDrfResult>(options.etats)
    const result: ContratCxpiCreateDTO = {
      typeDemande: 'ReservationFonds',
      dispositif: 'FPE',
      sousDispositif: etat.sousDispositif as SousDispositifDRF,
      typePublic: 'Interimaire',
      siret: etat.etablissement.siret.valeur!,
      idcc: etat.idcc.code,
      consentementTraitementDonnees: true,
    }
    return result
  },
  disposeCallback: () => useMachineEtatDisplay().hide(),
}

/* ── Clone DRF ── */

const ETAT_CLONE_HABILITATIONS_CHARGÉES = 'ETAT_CLONE_HABILITATIONS_CHARGÉES' as const
const transitionCloneChargementHabilitations: Runnable<TransitionChargementHabilitationsIn, CreationDrfHabilitationsChargées> = {
  run: async (options) => {
    const habilitations = await allServices.getHabilitations('ReservationFonds', options.modelIn.siret, options.modelIn.idcc)
    return {
      out: { habilitations },
      type: 'automatique',
    }
  },
}

const ETAT_CLONE_ONGLETS_SELECTIONNÉS = 'ETAT_CLONE_ONGLETS_SELECTIONNÉS' as const
const transitionCloneSelectionnerOnglets: Runnable<SelectionnerOngletsIn, SelectionnerOngletsOut> = {
  run: fabriqueRunCarte(ChoixOnglets),
}

const transitionCloneChargementEtablissementDepuisSiret: Runnable<string, CreationDrfResultChoixEtablissement> = {
  run: async (options) => {
    const etablissement = (await allServices.searchEtablissementAdherent(options.modelIn))!.at(0)!
    return {
      out: { etablissement },
      type: 'automatique',
    }
  },
}

const transitionsCloneDrf: Transitions<CloneDrfLaunch> = [
  /* 1. Charger les habilitations source (pour la carte onglets) */
  {
    etatDepart: null,
    etatArrive: ETAT_CLONE_HABILITATIONS_CHARGÉES,
    runnable: transitionCloneChargementHabilitations,
    queryIn: ({ launchParams }) => ({
      siret: launchParams!.siret,
      idcc: launchParams!.idcc,
    }),
  } as Transition<TransitionChargementHabilitationsIn, CreationDrfHabilitationsChargées, CloneDrfLaunch>,
  /* 2. Sélection des onglets à dupliquer */
  {
    etatDepart: ETAT_CLONE_HABILITATIONS_CHARGÉES,
    etatArrive: ETAT_CLONE_ONGLETS_SELECTIONNÉS,
    runnable: transitionCloneSelectionnerOnglets,
    queryIn: ({ launchParams, contextCourant, etats }) => ({
      typeDemande: 'ReservationFonds',
      context: contextCourant,
      dispositif: 'FPE',
      sousDispositif: launchParams!.sousDispositif,
      habilitations: (etats[ETAT_CLONE_HABILITATIONS_CHARGÉES] as CreationDrfHabilitationsChargées).habilitations,
      permissions: launchParams!.permissions,
    }),
  } as Transition<SelectionnerOngletsIn, SelectionnerOngletsOut, CloneDrfLaunch>,
  /* 3a. Si Employeur PAS sélectionné → charger les rattachements */
  {
    etatDepart: ETAT_CLONE_ONGLETS_SELECTIONNÉS,
    etatArrive: ETAT_RATTACHEMENT_CHARGÉS,
    runnable: transitionChargerRattachements,
    if: ({ etats }) => !(etats[ETAT_CLONE_ONGLETS_SELECTIONNÉS] as string[]).includes(ONGLET_EMPLOYEUR),
  } as Transition<void, CreationDrfRattachementsChargés, CloneDrfLaunch>,
  /* 3b. Sélection du rattachement */
  {
    etatDepart: ETAT_RATTACHEMENT_CHARGÉS,
    etatArrive: ETAT_RATTACHEMENT_SELECTIONNÉ,
    runnable: transitionSelectionnerRattachement,
    queryIn: (options) => fusionneEtats(options.etats).rattachements,
  } as Transition<RattachementDTO[], CreationDrfResultChoixRattachement, CloneDrfLaunch>,
  /* 3c. Sélection de l'établissement (autocomplete) */
  {
    etatDepart: ETAT_RATTACHEMENT_SELECTIONNÉ,
    etatArrive: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    runnable: transitionSelectionnerEtablissement,
    queryIn: (options) => fusionneEtats(options.etats).rattachement,
  } as Transition<RattachementDTO, CreationDrfResultChoixEtablissement, CloneDrfLaunch>,
  /* 3d. Si Employeur sélectionné → charger l'établissement depuis le siret source */
  {
    etatDepart: ETAT_CLONE_ONGLETS_SELECTIONNÉS,
    etatArrive: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    runnable: transitionCloneChargementEtablissementDepuisSiret,
    queryIn: ({ launchParams }) => launchParams!.siret,
    if: ({ etats }) => (etats[ETAT_CLONE_ONGLETS_SELECTIONNÉS] as string[]).includes(ONGLET_EMPLOYEUR),
  } as Transition<string, CreationDrfResultChoixEtablissement, CloneDrfLaunch>,
  /* 4. Vérification établissement actif */
  {
    etatDepart: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    etatArrive: ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ,
    runnable: transitionMessageInfo,
    if: (options) => {
      const etablissement = fusionneEtats<CreationDrfResult>(options.etats).etablissement
      return etablissement.etatAdministratif !== 'A'
    },
    queryIn: () =>
      'Vous ne pouvez sélectionner un établissement inactif, merci de sélectionner un autre établissement.',
  } as Transition<InformationMessageIn, void, CloneDrfLaunch>,
  /* 5. Sélection IDCC */
  {
    etatDepart: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    etatArrive: ETAT_IDCC_SELECTIONNÉ,
    runnable: transitionSelectionnerIdcc,
    queryIn: (options) => fusionneEtats(options.etats).etablissement,
    if: (options) => {
      const etablissement = fusionneEtats<CreationDrfResult>(options.etats).etablissement
      return etablissement.etatAdministratif === 'A'
    },
  } as Transition<EtablissementAdherentDTO, CreationDrfResultChoixIdcc, CloneDrfLaunch>,
  /* 6. Vérification IDCC TT */
  {
    etatDepart: ETAT_IDCC_SELECTIONNÉ,
    etatArrive: ETAT_IDCC_NON_TT_SELECTIONNÉ,
    runnable: transitionMessageInfo,
    queryIn: () => "L'Idcc de votre établissement n'est pas éligible au DRF.",
    if: (options) => {
      const etat = fusionneEtats<CreationDrfResult>(options.etats)
      return !idccAutoriseTT.includes(etat.idcc.code)
    },
  } as Transition<InformationMessageIn, void, CloneDrfLaunch>,
  /* 7. Chargement des habilitations pour l'établissement sélectionné */
  {
    etatDepart: ETAT_IDCC_SELECTIONNÉ,
    etatArrive: ETAT_HABILITATIONS_CHARGÉES,
    runnable: transitionChargementHabilitations,
    queryIn: (options) => {
      const etat = fusionneEtats<CreationDrfResult>(options.etats)
      return {
        siret: etat.etablissement.siret.valeur,
        idcc: etat.idcc.code,
      }
    },
    if: (options) => {
      const etat = fusionneEtats<CreationDrfResult>(options.etats)
      return idccAutoriseTT.includes(etat.idcc.code)
    },
  } as Transition<TransitionChargementHabilitationsIn, CreationDrfHabilitationsChargées, CloneDrfLaunch>,
  /* 8. Vérification habilitations */
  {
    etatDepart: ETAT_HABILITATIONS_CHARGÉES,
    etatArrive: ETAT_HABILITATIONS_MANQUANTES,
    runnable: transitionMessageInfo,
    queryIn: () => 'Vos habilitations ne permettent pas de créer une DRF pour cet établissement.',
    if: (options) => {
      const etat = fusionneEtats<CreationDrfResult>(options.etats)
      return !estHabilitéCXPI(etat.habilitations)
    },
  } as Transition<InformationMessageIn, void, CloneDrfLaunch>,
]

export const CloneDrfConfiguration: MachineEtatOptions<CloneDrfLaunch, ContratCxpiCloneDTO> = {
  transitions: transitionsCloneDrf,
  resultCallback: ({ etats, launchParams }) => {
    const etat = fusionneEtats<CreationDrfResult>(etats)
    return {
      typeDemande: 'ReservationFonds',
      id: launchParams!.idDemande,
      dispositif: 'FPE',
      sousDispositif: launchParams!.sousDispositif,
      typePublic: 'Interimaire',
      siret: etat.etablissement.siret.valeur!,
      idcc: etat.idcc.code,
      consentementTraitementDonnees: true,
      onglets: etats[ETAT_CLONE_ONGLETS_SELECTIONNÉS],
    } as ContratCxpiCloneDTO
  },
  disposeCallback: () => useMachineEtatDisplay().hide(),
}
